import {AfterViewInit, Component, OnDestroy, OnInit, SecurityContext, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DomSanitizer} from '@angular/platform-browser';
import {Subscription} from 'rxjs';
import {NgxMasonryComponent} from 'ngx-masonry';
import {AppComponent} from '../app.component';
import {UrlService} from '../model/url.service';
import {GlobalService} from '../model/global.service';
import {ComponentsService} from '../model/components.service';
import {SugarService} from '../model/sugar.service';
import {MetaData} from '../shared/metadata/metadata.service';
import {ImagesPresentationModule} from '../images-presentation/images-presentation.module';
import {ModalFormComponent} from '../modal-form/modal-form.component';
import {SocialShareComponent} from '../social-share/social-share.component';
import {UniversalPlatformService} from '../shared/platform/universal-platform.service';
import {ExhibitionSingleItemSetter} from '../shared/collections/api-collection-setters';
import * as imagesLoaded from 'imagesloaded';
import {AutoUnsubscribe} from '../decorators/autounsubscribe.decorator';
import {getIndicesOf} from '../helpers/string-helpers';
import {getExpandText} from '../helpers/helpers';

@AutoUnsubscribe()
@Component({
  selector: 'app-exhibitions-single',
  templateUrl: './exhibition-item.component.html',
})
export class ExhibitionItemComponent implements OnInit, AfterViewInit, OnDestroy {
  archiveExhibition = {
    pl: 'Wystawa archiwalna',
    en: 'Past exhibition',
  };
  public id: number;
  public _exhibition: { [key: string]: any };
  public _works: any;
  public _worksPagination: any;
  public _events: any;

  public breadcrumbRootsTitle: { [key: string]: string };
  @ViewChild('masonry')
  masonry: NgxMasonryComponent;
  @ViewChild('presentation')
  imagesPresentation: ImagesPresentationModule;
  @ViewChild('recommendationModal')
  recommendationModal: ModalFormComponent;
  @ViewChild('socialShare')
  socialShare: SocialShareComponent;
  isMasonry: 'true' | 'false';
  isFourColumns: 'true' | 'false';
  zoomImages: boolean;
  activeTextElementsOnHoverEnabled = false;
  cutDescription: boolean;
  cutDescriptionMaxLength: number;
  isDescriptionExpanded = false;
  private routeSub: Subscription;
  private rootSub: Subscription;
  private dataSub: Subscription;
  private _preloadingImages: boolean;

  constructor(
    public domSanitizer: DomSanitizer,
    public globalService: GlobalService,
    public urlService: UrlService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private componentsService: ComponentsService,
    private platformService: UniversalPlatformService,
    private sugarService: SugarService,
    private rootComp: AppComponent,
    private metadata: MetaData,
  ) {
    this.activeTextElementsOnHoverEnabled = this.globalService.settings.global.activeTextElementsOnHoverEnabled === 'true';
    this.cutDescription = this.globalService.settings.global.cutDescriptions === 'true';
    this.cutDescriptionMaxLength = Number(this.globalService.settings.global.cutDescriptionMaxLength);
    if (isNaN(this.cutDescriptionMaxLength)) {
      this.cutDescriptionMaxLength = 400;
    }
    this.zoomImages = this.globalService.settings.global.sliderRotation === 'true';
    this.routeSub = this.route.params.subscribe(params => {
      this.id = parseInt(params['id'], 10);
    });

    this.rootSub = this.componentsService.getRootComponents().subscribe(apiData => {
      const data = apiData ? <any>apiData : null;

      if (data && data.main_contents && data.main_contents.title) {
        this.breadcrumbRootsTitle = data.main_contents.title;
      }
    });

    this.globalService.loadGlobalSettings().subscribe(settings => {
      if (settings.__loaded) {
        this.isMasonry = settings.global.isMasonry;
        this.isFourColumns = settings.global.isFourColumns;
      }
    });

    this._preloadingImages = false;
  }

  get getExpandText() {
    return getExpandText(this.isDescriptionExpanded);
  }

  ngOnInit(): void {
    this.rootComp.fbService.updateCommonOg();

    this.sugarService.setCollection('exhibition', {
      sugarNode: 'exhibitions/' + this.id,
      parameters: {
        expand: 'images,artists,attachments',
      },
    });

    this.sugarService.setCollection('exhibition-catalogue', {
      sugarNode: 'exhibitions/' + this.id + '/products',
      parameters: {
        expand: 'images,artists,exhibition_item',
        // "filter[active]": '1',
      },
    });

    this.sugarService.setCollection('exhibition-events', {
      sugarNode: 'exhibitions/' + this.id + '/events',
      parameters: {
        expand: 'images',
      },
    });

    const itemSetter = new ExhibitionSingleItemSetter(
      this.urlService,
      this.platformService,
      this.sanitizer,
      this.sugarService,
    );

    this.dataSub = this.sugarService.getData().subscribe(data => {
      if (data.collectionId === 'exhibition') {
        const exhibition = itemSetter.setup(data.data);

        this.metadata.setItemPageMetaFromProperties(exhibition, 'banner');

        this._exhibition = exhibition;

        this._exhibition.url = this.urlService.fullLink('exhibitions') + '/' + this._exhibition.id;
      }

      if (data.collectionId === 'exhibition-catalogue') {
        const images: any[] = [];
        let image: any;
        let work: any;

        this._preloadingImages = true;
        this._works = itemSetter.setupWorks(data.data);

        for (let w = 0; w < (this._works || []).length; ++w) {
          work = this._works[w];

          if (work.images && (work.images || []).length) {
            image = new Image();
            image.src = work.images[0] ? work.images[0].thumbnails.slideshow.url : 'assets/images/placeholder-450x340.jpg';
            images.push(image);
          }
        }

        const imgLoaded = imagesLoaded(images);

        // wszystkie załadowane
        imgLoaded.on('done', () => {
          this._afterPreload();
        });

        // przynajmniej jeden zwrócił błąd
        imgLoaded.on('fail', () => {
          this._afterPreload();
        });

        this._worksPagination = data.pagination;
      }

      if (data.collectionId === 'exhibition-events') {
        this._events = itemSetter.setupEvents(data.data);
      }
    });

    this.sugarService.refreshData('exhibition');
    this.sugarService.refreshData('exhibition-catalogue');
    this.sugarService.refreshData('exhibition-events');
  }

  description() {
    const plain = (this.domSanitizer.sanitize(SecurityContext.HTML, this._exhibition.description[this.urlService.langCode]) || '').trim();
    const spacesPositions = getIndicesOf(plain, ' ');

    this.cutDescriptionMaxLength = spacesPositions.filter(pos => pos >= this.cutDescriptionMaxLength)[0];

    this.cutDescription = this.cutDescription && (plain || []).length > this.cutDescriptionMaxLength;

    return this.cutDescription && !this.isDescriptionExpanded
      ? (plain.slice(0, this.cutDescriptionMaxLength)) + '...'
      : this._exhibition.description[this.urlService.langCode];
  }

  toggleDescription() {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.rootComp.bodyClass = 'single-exhibition';
    });
  }

  ngOnDestroy(): void {
    this.routeSub.unsubscribe();

    if (this.rootSub) {
      this.rootSub.unsubscribe();
    }

    if (this.dataSub) {
      this.dataSub.unsubscribe();
    }
  }

  openRecommendationModal(evt?: any): void {

    if (evt && typeof evt.prefentDefault !== 'undefined') {
      evt.preventDefault();
    }

    this.recommendationModal.openModal();
  }

  showPreloader(): boolean {
    if (!this._exhibition) {
      return true;
    }

    return this._preloadingImages;
  }

  private _afterPreload(): void {
    this._preloadingImages = false;

    if (this.masonry) {
      setTimeout(() => this.masonry.layout(), 100);
    }
  }

}
